import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { storage } from '../utils/storage'

const ImmersiveContext = createContext()

/**
 * ImmersiveProvider - Manages immersive mode tab navigation and panel state
 * Provides centralized state for tab switching, panel minimization, and navigation history
 */
export function ImmersiveProvider({ children }) {
  const [activeTab, setActiveTab] = useState('search')
  const [panelState, setPanelState] = useState('expanded') // 'expanded', 'minimized', 'hidden'
  const [previousTab, setPreviousTab] = useState(null)
  
  // Track navigation history for tab switching (last 10 tabs)
  const [tabHistory, setTabHistory] = useState(['search'])
  
  // Handle tab change with history tracking
  const changeTab = useCallback((newTab, newPanelState = null) => {
    // Handle minimize signal
    if (newTab === '_minimize_') {
      setPanelState('minimized')
      return
    }
    
    // Don't change if already on this tab
    if (newTab === activeTab && !newPanelState) return
    
    // Update history (keep last 10 entries)
    setTabHistory(prev => {
      const newHistory = [...prev, newTab]
      return newHistory.slice(-10)
    })
    
    setPreviousTab(activeTab)
    setActiveTab(newTab)
    
    // Update panel state if specified
    if (newPanelState) {
      setPanelState(newPanelState)
      storage.set('immersive_panelState', newPanelState)
    } else if (panelState === 'minimized') {
      // Auto-expand when changing tabs from minimized
      setPanelState('expanded')
      storage.set('immersive_panelState', 'expanded')
    }
    
    // Persist active tab
    storage.set('immersive_activeTab', newTab)
  }, [activeTab, panelState])
  
  // Navigate to previous tab
  const goBack = useCallback(() => {
    if (previousTab) {
      setActiveTab(previousTab)
      setPreviousTab(null)
      storage.set('immersive_activeTab', previousTab)
    } else if (tabHistory.length > 1) {
      const previous = tabHistory[tabHistory.length - 2]
      setActiveTab(previous)
      setTabHistory(prev => prev.slice(0, -1))
      storage.set('immersive_activeTab', previous)
    }
  }, [previousTab, tabHistory])
  
  // Toggle panel between expanded and minimized
  const togglePanel = useCallback(() => {
    setPanelState(prev => {
      const newState = prev === 'expanded' ? 'minimized' : 'expanded'
      storage.set('immersive_panelState', newState)
      return newState
    })
  }, [])
  
  // Minimize panel
  const minimizePanel = useCallback(() => {
    setPanelState('minimized')
    storage.set('immersive_panelState', 'minimized')
  }, [])
  
  // Expand panel
  const expandPanel = useCallback(() => {
    setPanelState('expanded')
    storage.set('immersive_panelState', 'expanded')
  }, [])
  
  // Hide panel completely
  const hidePanel = useCallback(() => {
    setPanelState('hidden')
    storage.set('immersive_panelState', 'hidden')
  }, [])
  
  // Show panel (restore to expanded)
  const showPanel = useCallback(() => {
    setPanelState('expanded')
    storage.set('immersive_panelState', 'expanded')
  }, [])
  
  // Restore state on mount
  useEffect(() => {
    const savedTab = storage.get('immersive_activeTab', 'search')
    const savedPanelState = storage.get('immersive_panelState', 'expanded')
    setActiveTab(savedTab)
    setPanelState(savedPanelState)
    setTabHistory([savedTab])
  }, [])
  
  const value = {
    // State
    activeTab,
    panelState,
    previousTab,
    tabHistory,
    
    // Tab navigation
    changeTab,
    goBack,
    
    // Panel controls
    togglePanel,
    minimizePanel,
    expandPanel,
    hidePanel,
    showPanel,
    
    // Computed
    canGoBack: previousTab !== null || tabHistory.length > 1,
    isExpanded: panelState === 'expanded',
    isMinimized: panelState === 'minimized',
    isHidden: panelState === 'hidden'
  }
  
  return <ImmersiveContext.Provider value={value}>{children}</ImmersiveContext.Provider>
}

/**
 * useImmersive - Access immersive mode context
 * Must be used within ImmersiveProvider
 */
export function useImmersive() {
  const context = useContext(ImmersiveContext)
  if (!context) {
    throw new Error('useImmersive must be used within ImmersiveProvider')
  }
  return context
}

export default ImmersiveContext
